// mds-dojo — dojo/Stateful — FAITHFUL port of references/dojo/Stateful.js.
// Named properties with optional _xxxGetter/_xxxSetter accessors + watch(). Base
// class for _WidgetBase. Ported via the codemod pipeline (define→ESM+declare),
// declare from the faithful runtime.
import declare from './_base/declare-runtime';
import array from './_base/array';
import when from './when';

export default declare('dojo.Stateful', null, {
  // Cache of attribute name → {s:_<name>Setter, g:_<name>Getter}, shared across
  // instances (a prototype field, exactly as dojo).
  _attrPairNames: {},

  _getAttrNames(this: any, name: string): any {
    const apn = this._attrPairNames;
    if (apn[name]) { return apn[name]; }
    return (apn[name] = { s: '_' + name + 'Setter', g: '_' + name + 'Getter' });
  },

  postscript(this: any, params?: any): void {
    // Automatic setting of params during construction.
    if (params) { this.set(params); }
  },

  _get(this: any, name: string, names: any): any {
    return typeof this[names.g] === 'function' ? this[names.g]() : this[name];
  },

  get(this: any, name: string): any {
    return this._get(name, this._getAttrNames(name));
  },

  set(this: any, name: any, value?: any): any {
    // If an object is used, iterate through the object.
    if (typeof name === 'object') {
      for (const x in name) {
        if (name.hasOwnProperty(x) && x != '_watchCallbacks' && x != '__proto__') {
          this.set(x, name[x]);
        }
      }
      return this;
    }

    const names = this._getAttrNames(name);
    const oldValue = this._get(name, names);
    const setter = this[names.s];
    let result: any;
    if (typeof setter === 'function') {
      // use the explicit setter
      result = setter.apply(this, Array.prototype.slice.call(arguments, 1));
    } else {
      // no setter so set attribute directly
      this[name] = value;
    }
    if (this._watchCallbacks) {
      const self = this;
      // If setter returned a promise, wait for it; otherwise notify immediately.
      when(result, function () {
        self._watchCallbacks(name, oldValue, value);
      });
    }
    return this;
  },

  _changeAttrValue(this: any, name: string, value: any): any {
    const oldValue = this.get(name);
    this[name] = value;
    if (this._watchCallbacks) {
      this._watchCallbacks(name, oldValue, value);
    }
    return this;
  },

  watch(this: any, name: any, callback?: any): any {
    let callbacks = this._watchCallbacks;
    if (!callbacks) {
      const self = this;
      callbacks = this._watchCallbacks = function (name: string, oldValue: any, value: any, ignoreCatchall?: boolean) {
        const notify = function (propertyCallbacks: any) {
          if (propertyCallbacks) {
            propertyCallbacks = propertyCallbacks.slice();
            for (let i = 0, l = propertyCallbacks.length; i < l; i++) {
              propertyCallbacks[i].call(self, name, oldValue, value);
            }
          }
        };
        notify(callbacks['_' + name]);
        if (!ignoreCatchall) {
          notify(callbacks['*']); // the catch-all
        }
      };
    }
    if (!callback && typeof name === 'function') {
      callback = name;
      name = '*';
    } else {
      // prepend with dash to prevent name conflicts with function (like "name")
      name = '_' + name;
    }
    let propertyCallbacks = callbacks[name];
    if (typeof propertyCallbacks !== 'object') {
      propertyCallbacks = callbacks[name] = [];
    }
    propertyCallbacks.push(callback);

    const handle: any = {};
    handle.unwatch = handle.remove = function () {
      const index = array.indexOf(propertyCallbacks, callback);
      if (index > -1) {
        propertyCallbacks.splice(index, 1);
      }
    };
    return handle;
  },
});
